export type CompactWorkflowActividad = {
  nodoId: number;
  habilitado: boolean;
  requiereAutorizacion: boolean;
  tieneUsuario: boolean;
  tieneAutoriza: boolean;
};

export type CompactWorkflowEmpresa = {
  id: number;
  valido: boolean;
  warning: boolean;
  bloqueado: boolean;
  instanciaEstado: string | null;
  actividades: CompactWorkflowActividad[];
};

export type CompactWorkflow = {
  id?: number;
  nombre?: string;
  periodos: Array<{ tipo?: string | null; numero?: number | null }>;
  empresas: CompactWorkflowEmpresa[];
};

export type CompactEmpresa = {
  id?: number;
  rfc?: string;
  razonSocial?: string;
  tieneRegimenes: boolean;
  tieneObligaciones: boolean;
  tieneCiec: boolean;
  tieneArchivosSat: boolean;
  tieneSellos: boolean;
};

function isHabilitado(value: unknown): boolean {
  return value === true || value === 'HABILITADA';
}

type WorkflowInput = {
  id?: number;
  nombre?: string;
  periodos?: Array<{
    tipo?: string | null;
    tipoPeriodo?: string | null;
    numero?: number | null;
    numeroPeriodo?: number | null;
    id?: number;
  }>;
  empresasSeleccionadas?: Array<{
    id: number;
    valido?: boolean;
    warning?: boolean;
    bloqueado?: boolean;
    instanciaEstado?: string | null;
    actividades?: Record<string, {
      nodoId?: number;
      nodo?: { id?: number };
      habilitado?: unknown;
      requiereAutorizacion?: unknown;
      usuario?: unknown;
      autoriza?: unknown;
    }>;
  }>;
};

type EmpresaInput = {
  info?: Record<string, unknown>;
  regimenes?: unknown[];
  obligacionesFiscales?: unknown[];
  archivosSat?: unknown[];
  archivosSellos?: unknown[];
  ciec?: unknown;
};

function nonempty(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

export function compactWorkflowConfig(raw: WorkflowInput): CompactWorkflow {
  const empresas = (raw.empresasSeleccionadas ?? []).map(item => {
    const actividades = Object.entries(item.actividades ?? {}).flatMap(([key, entry]) => {
      const nodoId = Number(entry.nodoId ?? entry.nodo?.id ?? key);
      if (Number.isNaN(nodoId)) return [];
      return [{
        nodoId,
        habilitado: isHabilitado(entry.habilitado),
        requiereAutorizacion: !!entry.requiereAutorizacion,
        tieneUsuario: entry.usuario != null,
        tieneAutoriza: entry.autoriza != null,
      }];
    });
    return {
      id: item.id,
      valido: !!item.valido,
      warning: !!item.warning,
      bloqueado: !!item.bloqueado,
      instanciaEstado: item.instanciaEstado ?? null,
      actividades,
    };
  });
  return {
    id: raw.id,
    nombre: raw.nombre,
    periodos: (raw.periodos ?? []).map(periodo => ({
      tipo: periodo.tipo ?? periodo.tipoPeriodo,
      numero: periodo.numero ?? periodo.numeroPeriodo ?? null,
    })),
    empresas,
  };
}

export function compactEmpresaConfig(raw: EmpresaInput): CompactEmpresa {
  const info = raw.info ?? {};
  const ciec = info.ciec ?? info.cciec ?? raw.ciec;
  return {
    id: (info.id as number | undefined) ?? undefined,
    rfc: info.rfc as string | undefined,
    razonSocial: info.razonSocial as string | undefined,
    tieneRegimenes: Array.isArray(raw.regimenes) && raw.regimenes.length > 0,
    tieneObligaciones: Array.isArray(raw.obligacionesFiscales) && raw.obligacionesFiscales.length > 0,
    tieneCiec: nonempty(ciec),
    tieneArchivosSat: Array.isArray(raw.archivosSat) && raw.archivosSat.length > 0,
    tieneSellos: Array.isArray(raw.archivosSellos) && raw.archivosSellos.length > 0,
  };
}
